import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReactNode, useRef, useState } from 'react';
import { ContainerCloneCell } from '../components/ContainerCloneCell/ContainerCloneCell';
import { RenderAssignReasonFormProps } from '../../../forms/AssignReasonForm';
import { onClickModalReason } from '../TimeGrid.types';
import {
  AddTechnicalStopFunction,
  PopoverAddMotiveLabelsProps,
} from '../components/PopoverAddMotive/PopoverAddMotive.types';

dayjs.extend(customParseFormat);

const CLONE_DIV_ID = 'clonedNode-cell';
const TOTAL_MINUTES = 60;

interface DataCellProps {
  startMinute: number;
  endMinute: number;
  hourSelected: number;
  id: number;
  count: number;
  width: number;
  height: number;
  offsetLeftFirstCell: number;
  maxLeft: number;
  maxRight: number;
  reason?: string;
  stopReasonId?: number;
  stopType?: string;
  color?: string;
  machineId?: number;
  componentId?: number;
  category?: {
    name: string;
  };
  stopTypeName?: string;
  machineName?: string;
  categoryName?: string;
  note?: string;
  failureModeId?: number | null;
  failureModeName?: string | null;
}

interface StopProps {
  reason: string;
  machineId?: number;
  componentId?: number;
  stopReasonId: number;
  stopTypeName?: string;
  stopType?: string;
  machineName?: string;
  categoryName?: string;
  note?: string;
  failureModeId?: number | null;
  failureModeName?: string | null;
}

const getPositionAndSize = () => {
  const minuteCells = document.querySelectorAll('.minute-header');
  const firstMinute = minuteCells[0] as HTMLDivElement;
  const rows = document.querySelectorAll('.cells.row');
  return {
    top: firstMinute.offsetTop + firstMinute.offsetHeight + 1,
    left: firstMinute.offsetLeft + 1,
    height: Array.from(rows).reduce((total, cell) => {
      return total + (cell as HTMLDivElement).getBoundingClientRect().height;
    }, rows.length - 1),
    width: Array.from(minuteCells).reduce((total, cell) => {
      return total + (cell as HTMLDivElement).getBoundingClientRect().width;
    }, 0),
  };
};

const createCloneDiv = () => {
  const dataPosition = getPositionAndSize();
  const style = {
    position: 'absolute',
    top: `${dataPosition.top}px`,
    left: `${dataPosition.left}px`,
    height: `${dataPosition.height}px`,
    width: `${dataPosition.width}px`,
    zIndex: 10,
    borderStyle: 'none',
  };

  return React.createElement('div', { id: CLONE_DIV_ID, style: style });
};

const getFirstMinuteCell = (target: HTMLDivElement) => {
  let currentDiv = target;
  while (currentDiv.previousSibling) {
    if (
      currentDiv.previousSibling instanceof HTMLElement &&
      currentDiv.previousSibling.dataset.firstMinute
    ) {
      currentDiv = currentDiv.previousSibling as HTMLDivElement;
    } else {
      break;
    }
  }
  return currentDiv.getBoundingClientRect().left;
};

const getLeftMaxWidthResize = (target: HTMLDivElement) => {
  let currentDiv = target;
  while (currentDiv.previousSibling) {
    if (
      currentDiv.previousSibling instanceof HTMLElement &&
      currentDiv.previousSibling.dataset.isSuperimposable
    ) {
      currentDiv = currentDiv.previousSibling as HTMLDivElement;
    } else {
      break;
    }
  }
  return currentDiv.offsetLeft;
};

const getRightMaxWidthResize = (target: HTMLDivElement) => {
  let currentDiv = target;
  while (currentDiv.nextSibling) {
    if (
      currentDiv.nextSibling instanceof HTMLElement &&
      currentDiv.nextSibling.dataset.isSuperimposable
    ) {
      currentDiv = currentDiv.nextSibling as HTMLDivElement;
    } else {
      break;
    }
  }
  return currentDiv.offsetLeft + currentDiv.offsetWidth;
};

const getMaxWidthResize = (target: HTMLDivElement) => {
  return {
    maxLeft: getLeftMaxWidthResize(target),
    maxRight: getRightMaxWidthResize(target),
  };
};

const getCellClicked = (target: HTMLElement): HTMLDivElement => {
  if (target.classList.contains('cell')) return target as HTMLDivElement;
  return getCellClicked(target.parentElement as HTMLElement);
};

export const useClickStopPopover = (
  setDivResize: (element?: JSX.Element) => void,
  onClickOpenModalReason: onClickModalReason,
  labelsPopoverAddStop: PopoverAddMotiveLabelsProps,
  timezone: string,
  hours: number[],
  onClickAddTechnicalStop: AddTechnicalStopFunction,
  renderAssignReasonForm: (assignReasonFormProps: RenderAssignReasonFormProps) => ReactNode,
  showAddTechnicalStopMenu?: boolean,
  onClickDeleteStop?: (id: number, onCloseModal: () => void) => void
) => {
  const [showBackdrop, setShowBackdrop] = useState(false);
  const popRef = useRef<HTMLIonPopoverElement>(null);

  const removeDinamicNode = () => {
    setDivResize();
    setShowBackdrop(false);
  };

  const handleCloseModal = () => {
    if (popRef.current) {
      popRef.current.dismiss();
      removeDinamicNode();
    }
  };

  const getDataCell = (target: HTMLDivElement): DataCellProps => {
    return {
      startMinute: Number(target.dataset.minInit),
      endMinute: Number(target.dataset.minEnd),
      hourSelected: Number(target.dataset.hour),
      id: Number(target.dataset.id),
      count: Number(target.dataset.count),
      width: target.offsetWidth,
      height: target.offsetHeight,
      offsetLeftFirstCell: getFirstMinuteCell(target),
      color: target.dataset.color,
      ...getMaxWidthResize(target),
    };
  };

  const render = (
    clonedNode: React.ReactElement,
    dataTarget: DataCellProps,
    event: React.MouseEvent<HTMLElement>,
    isEdit: boolean
  ) => {
    const clonedCellWidth = Number(clonedNode.props.style.width.replace('px', ''));
    return (
      <ContainerCloneCell
        resizeCellProps={{
          height: dataTarget.height,
          width: dataTarget.width,
          color: dataTarget.color || '',
        }}
        popoverAddMotiveProps={{
          id: dataTarget.id,
          onClickOpenModalReason: onClickOpenModalReason,
          handleCloseModal: handleCloseModal,
          timeRange: [0, 0],
          popRef: popRef,
          timezone,
          reason: dataTarget.reason,
          stopReasonId: dataTarget.stopReasonId,
          stopType: isEdit ? dataTarget?.stopType ?? '' : '',
          isResized: false,
          originalTimeRange: [0, 0],
          onClickAddTechnicalStop,
          showAddTechnicalStopMenu,
          machineId: dataTarget.machineId,
          componentId: dataTarget.componentId,
          isEdit,
          renderAssignReasonForm,
          categoryName: dataTarget.categoryName || '',
          stopTypeName: dataTarget.stopTypeName || '',
          machineName: dataTarget.machineName || '',
          note: dataTarget.note,
          failureModeId: dataTarget.failureModeId,
          failureModeName: dataTarget.failureModeName,
          onClickDeleteStop,
        }}
        labelsPopoverAddStop={labelsPopoverAddStop}
        timeRange={[dataTarget.startMinute, dataTarget.endMinute]}
        widthPerMinute={clonedCellWidth / TOTAL_MINUTES}
        totalHeight={Number(clonedNode.props.style.height.replace('px', ''))}
        totalWidth={clonedCellWidth}
        timezone={timezone}
        hours={hours}
        event={event}
        removeNode={removeDinamicNode}
      />
    );
  };

  const handleClickUpdateReason = (
    e: React.MouseEvent<HTMLElement>,
    propsElement: StopProps
  ) => {
    setShowBackdrop(true);
    const target = getCellClicked(e.target as HTMLElement);
    const clonedNode = createCloneDiv();

    setDivResize(
      React.cloneElement(
        clonedNode,
        { id: CLONE_DIV_ID },
        render(clonedNode, { ...getDataCell(target), ...propsElement }, e, true)
      )
    );
  };

  const handleClickStop = (
    e: React.MouseEvent<HTMLElement>,
    propsElement: { stopType: string }
  ) => {
    setShowBackdrop(true);
    const target = getCellClicked(e.target as HTMLElement);
    const clonedNode = createCloneDiv();

    setDivResize(
      React.cloneElement(
        clonedNode,
        { id: CLONE_DIV_ID },
        render(clonedNode, { ...getDataCell(target), ...propsElement }, e, false)
      )
    );
  };

  return {
    showBackdrop,
    setShowBackdrop,
    handleClickStop,
    handleClickUpdateReason,
    removeDinamicNode,
  };
};
